import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  ClipboardList,
  GraduationCap,
  Briefcase,
  TrendingUp,
  Users,
  BadgeCheck,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import StatCard from "@/components/shared/StatCard";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/institution/dashboard/")({
  component: InstitutionDashboard,
});

const activityVariant: Record<string, BadgeProps["variant"]> = {
  placement: "success",
  assessment: "warning",
  worklog: "info",
  badge: "secondary",
};

function InstitutionDashboard() {
  const { user } = useAuthStore();

  // Mock data - will be replaced with API calls
  const stats = {
    totalStudents: 342,
    activePlacements: 156,
    pendingAssessments: 23,
    avgCompletionRate: 78,
  };

  const recentActivities = [
    { id: 1, student: "John Mwamba", action: "Completed placement at CRDB Bank", time: "2 hours ago", type: "placement" },
    { id: 2, student: "Sarah Kimaro", action: "Submitted assessment #3", time: "4 hours ago", type: "assessment" },
    { id: 3, student: "David Msuya", action: "Logged 40 hours this week", time: "6 hours ago", type: "worklog" },
    { id: 4, student: "Grace Mushi", action: "Earned Project Management Badge", time: "1 day ago", type: "badge" },
  ];

  const topPerformers = [
    { id: 1, name: "John Mwamba", hours: 320, placement: "CRDB Bank" },
    { id: 2, name: "Sarah Kimaro", hours: 285, placement: "Vodacom" },
    { id: 3, name: "David Msuya", hours: 265, placement: "TANESCO" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Institution dashboard"
        subtitle="Monitor student placements, assessments, and performance across your institution"
        className="mb-0 md:mb-0"
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Students"
          value={stats.totalStudents}
          icon={<Users />}
          trend={{ value: "+12% this semester", positive: true }}
        />
        <StatCard
          label="Active Placements"
          value={stats.activePlacements}
          icon={<Briefcase />}
          trend={{ value: "Across 24 partners", positive: true }}
        />
        <StatCard
          label="Pending Assessments"
          value={stats.pendingAssessments}
          icon={<ClipboardList />}
          trend={{ value: "Needs grading", positive: false }}
        />
        <StatCard
          label="Avg Completion Rate"
          value={`${stats.avgCompletionRate}%`}
          icon={<TrendingUp />}
          trend={{ value: "+5% vs last year", positive: true }}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Recent Activity */}
        <SectionCard
          title="Recent student activity"
          className="lg:col-span-2"
          noPadding
          actions={
            <Button variant="ghost" size="sm">
              View all
              <ArrowRight />
            </Button>
          }
        >
          <ul className="divide-y divide-border">
            {recentActivities.map((activity) => (
              <li key={activity.id} className="flex items-start gap-3 px-4 py-3.5 md:px-5">
                <Avatar className="size-8 rounded-full">
                  <AvatarFallback className="rounded-full text-caption">
                    {activity.student.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{activity.student}</p>
                  <p className="text-sm text-body">{activity.action}</p>
                  <p className="mt-0.5 text-caption text-muted-foreground">{activity.time}</p>
                </div>
                <Badge variant={activityVariant[activity.type] ?? "secondary"} className="capitalize">
                  {activity.type}
                </Badge>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* Top Performers */}
        <SectionCard
          title="Top performers"
          actions={<BadgeCheck className="size-4 text-muted-foreground" aria-hidden />}
          noPadding
        >
          <ol className="divide-y divide-border">
            {topPerformers.map((student, index) => (
              <li
                key={student.id}
                className={cn(
                  "flex items-center justify-between gap-3 px-4 py-3.5 md:px-5",
                  index === 0 && "bg-muted/50",
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="w-5 shrink-0 font-mono text-code text-muted-foreground tabular-nums">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {index === 0 && "🥇 "}{index === 1 && "🥈 "}{index === 2 && "🥉 "}
                      {student.name}
                    </p>
                    <p className="truncate text-caption text-muted-foreground">{student.placement}</p>
                  </div>
                </div>
                <span className="shrink-0 font-mono text-code text-foreground tabular-nums">
                  {student.hours}h
                </span>
              </li>
            ))}
          </ol>
        </SectionCard>
      </div>

      {/* Quick Actions */}
      <SectionCard title="Quick actions">
        <div className="flex flex-wrap gap-2 max-sm:[&>*]:flex-1">
          <Button>
            <ClipboardList />
            Create assessment
          </Button>
          <Button variant="outline">
            <Users />
            Manage students
          </Button>
          <Button variant="outline">
            <Briefcase />
            Review placements
          </Button>
          <Button variant="outline">
            <GraduationCap />
            Institution profile
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}

export default InstitutionDashboard;
